import * as React from 'react'
// libs
import styled from 'styled-components'
import { formValueSelector } from 'redux-form'
import { useSelector } from 'react-redux'
// components
import DetailsForm from 'components/Modals/PlanItems/Action/MinimizedModal/components/DetailsSection/components/DetailsForm'
import { DrawerModal } from 'components/UI'
import { FloatingBottomPaper } from 'components/FormElements/FloatingActions/views'
// context
import { ActionsPageContext } from 'containers/Actions/Register/layers/API'
import { ActionCreationContext } from 'context/index'
// constants
import { ENTITIES } from 'constants/entities'
import { Forms, Modals } from 'constants/index'
// hooks
import { useModalData, useDefaultObjectSecurity } from 'hooks/index'
// themes
import { CloseIcon } from 'components/UI/Icons'
import { Header, Wrapper as Scrollbar } from 'components/Modals/PlanItems/styles'
// utils
import get from 'lodash/get'
import defaultTo from 'lodash/fp/defaultTo'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'
import { getFieldValues, getOptionsByEntity, getValueFromOption, getValuesFromOptions } from 'utils/form'

const PAPER_PROPS = {
  style: {
    height: '100%',
    overflow: 'hidden',
    top: 0
  }
}

const MODAL_PROPS = { disableBackdropClick: true }

const Wrapper = styled('div')`
  margin: 0 16px 80px 16px;

  ${FloatingBottomPaper} {
    width: 600px;
    right: 0;
  }
`

const selector = formValueSelector(Forms.ACTION_CREATE_FORM)

const ActionCreateModal: React.FC = () => {
  const { actionCreate, fields, refetch, planItemCreate } = React.useContext(ActionsPageContext)

  const { plan } = useSelector((state) => ({
    plan: selector(state, 'plan')
  }))

  const defaultObjectSecurities = useDefaultObjectSecurity({
    objectApiName: 'actions',
    planId: get(plan, 'value')
  })

  const {
    hideModal,
    modalData: { isOpen }
  } = useModalData({ modalType: Modals.ACTION_CREATE_MODAL })

  const handleFormSubmit = async (data) => {
    const parentId = getValueFromOption(data.parent)
    const planId = getValueFromOption(data.plan)

    const input = {
      commencementAt: data.commencementAt,
      completionAt: data.completionAt,
      completionPercentage: defaultTo(0, data.completionPercentage),
      description: data.description,
      fieldValues: getFieldValues(fields, data, defaultObjectSecurities),
      memberPositionIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.POSITION)),
      memberUserIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.USER)),
      milestone: data.isThisAMilestone,
      ownedById: getValueFromOption(data.ownedByEmployeeOrPosition)
    }

    try {
      planId
        ? await planItemCreate({
            variables: {
              input: {
                planId: planId,
                parentId: parentId || null,
                planActionInput: input
              }
            }
          })
        : await actionCreate({
            variables: {
              input: input
            }
          })

      await refetch()
      hideModal()
    } catch (error) {
      console.log('[ActionCreateModal.handleFormSubmit]: ', parseGraphQLErrorMessages(error))
    }
  }

  const initialValues = {
    completionPercentage: 0
  }

  const value = { fields, isCreateForm: true }

  return (
    <DrawerModal
      width={'600px'}
      isOpen={isOpen}
      hideModal={hideModal}
      ModalProps={MODAL_PROPS}
      PaperProps={PAPER_PROPS}>
      <Header>
        <span>Create Action</span>
        <CloseIcon onClick={hideModal} />
      </Header>
      <Scrollbar>
        <Wrapper>
          <ActionCreationContext.Provider value={value}>
            <DetailsForm
              form={Forms.ACTION_CREATE_FORM}
              initialValues={initialValues}
              onCancelClick={hideModal}
              onSubmit={handleFormSubmit}
            />
          </ActionCreationContext.Provider>
        </Wrapper>
      </Scrollbar>
    </DrawerModal>
  )
}

export default ActionCreateModal
